import { useEffect } from "react";
import {
  HeadingText,
  ImageMessage,
  ParagraphText,
  StackLayout,
} from "@leagueplatform/genesis-core";
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from "@leagueplatform/analytics";
import { useIntl } from "@leagueplatform/locales";
import { useGetUserProfile } from "@leagueplatform/web-common";
import {
  ErrorState,
  LoadingIndicator,
} from "@leagueplatform/web-common-components";
import { StandaloneAuth } from "@leagueplatform/auth-standalone";
import { terminateEmblemLiveChatSession } from "live-chat/emblem-live-chat-client";
import {
  DETAIL,
  QUERY_PARAM_KEYS,
  QUERY_PARAM_MODAL_VALUE,
} from "common/constants";
import { BaseModalContainer } from "components/modals/common/base-modal-container.component";
import { ModalActionButton } from "components/modals/common/modal-action-button.component";
import { useDeleteAccount } from "hooks/use-delete-account/use-delete-account.hook";
import exitWarning from "assets/exit-warning.svg";

type ProfileShape = { userId?: string };

type Props = { onClose: () => void };

export const DeleteAccountModal = ({ onClose }: Props) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
    });
  }, []);

  const { data, isLoading, error } = useGetUserProfile() as {
    data: ProfileShape | undefined;
    isLoading: boolean;
    error: Error | null;
  };

  const {
    mutateAsync,
    isLoading: isDeleting,
    isError,
    reset,
  } = useDeleteAccount();

  if (isLoading) {
    return (
      <BaseModalContainer>
        <LoadingIndicator />
      </BaseModalContainer>
    );
  }

  if (isError || error || !data?.userId) {
    return (
      <BaseModalContainer>
        <ErrorState error={error} onButtonClick={reset} />
      </BaseModalContainer>
    );
  }

  const handleDelete = async () => {
    await mutateAsync(data.userId!, {
      onSuccess: async () => {
        const params = new URLSearchParams();
        params.set(
          QUERY_PARAM_KEYS.MODAL,
          QUERY_PARAM_MODAL_VALUE.PERSONALIZATION_REMOVED_DISCLAIMER
        );
        onClose();
        // Route through the shared live-chat teardown facade before the
        // Auth0 redirect so the next member on the same browser can't
        // inherit this account's chat identity. Bounded await inside the
        // facade caps the wait if Genesys is stalled.
        await terminateEmblemLiveChatSession();
        // Non-federated so the Salesforce IdP session survives — only the
        // local Auth0 session is cleared.
        StandaloneAuth.client.logout({
          returnTo: `${window.location.origin}?${params.toString()}`,
        });
      },
    });
  };

  return (
    <BaseModalContainer
      footer={
        <StackLayout
          orientation={{ "@initial": "horizontal", "@mobile": "vertical" }}
          horizontalAlignment={{ "@initial": "end", "@mobile": "center" }}
          spacing="$one"
          css={{
            flexGrow: 0,
            "@mobile": { flexFlow: "column-reverse" },
          }}
        >
          <ModalActionButton
            actionType="cancel"
            buttonText={formatMessage({ id: "STR_NEVER_MIND" })}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: DETAIL.CANCEL,
            }}
          />
          <ModalActionButton
            actionType="submit"
            buttonText={formatMessage({ id: "STR_YES_DELETE_ACCOUNT" })}
            onClick={handleDelete}
            destructive
            loading={isDeleting}
            disabled={isDeleting}
            trackAnalytics={{
              product_area: PRODUCT_AREA.SETTINGS,
              screen_name: SCREEN_NAMES.DELETE_ACCOUNT,
              detail: DETAIL.CONFIRM_DELETION,
            }}
          />
        </StackLayout>
      }
    >
      <ImageMessage
        image={exitWarning}
        css={{
          flexGrow: 1,
          padding: "$two $half $two $half",
          textAlign: "left",
          "@mobile": { padding: "$half" },
        }}
      >
        <HeadingText
          level="2"
          size={{ "@initial": "lg", "@mobile": "xl" }}
          css={{ paddingBlock: "$one" }}
        >
          {formatMessage({ id: "STR_ARE_YOU_SURE" })}
        </HeadingText>
        <ParagraphText>
          {formatMessage({ id: "STR_DELETE_ACCOUNT_BODY_1" })}
        </ParagraphText>
        <ParagraphText
          css={{
            marginBlock: "$one",
            "@mobile": { marginBlock: "$none" },
          }}
        >
          {formatMessage({ id: "STR_DELETE_ACCOUNT_BODY_2" })}
        </ParagraphText>
      </ImageMessage>
    </BaseModalContainer>
  );
};
